import {gql} from '@apollo/client';
import {Badge, Group, Text, TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, GRAY_SEMIDARK, NEAR_BLACK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import useFormInput from 'ui-library/hooks/useFormInput';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import {postJSONPromise, putJSONPromise, isServerRequestError} from 'util/Net';

import type {
  AdminMcpConfigurationModalQuery,
  AdminMcpConfigurationModalQueryVariables,
} from './__generated/AdminMcpConfigurationModalQuery';
import McpAuthPrompt from './McpAuthPrompt';
import {getPermissionLabel, McpToolPermissionValue, sharedStyles} from './McpModalStyles';
import McpToolList, {ToolInfo} from './McpToolList';

export const ADMIN_MCP_CONFIG_QUERY = gql`
  query AdminMcpConfigurationModalQuery($serverId: StrictString!) @skipAudit @engComponent(name: LLM) {
    mcpServerConfig(id: $serverId) {
      id
      name
      serverUrl
      toolPermissions {
        toolName
        permission
      }
    }
  }
`;

interface RefreshToolsResponse {
  configId: string;
  needsRedirect: boolean;
  redirectUrl: string | null;
  toolCount?: number;
  tools: Array<{name: string; title: string | null; description: string; parameters: unknown}> | null;
}

interface AdminToolPermissionsModalProps {
  serverId: string;
  serverName: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  onSave?: () => void;
}

type PermissionState = McpToolPermissionValue | null;

export default function AdminMcpConfigurationModal(props: AdminToolPermissionsModalProps): JSX.Element {
  const {serverId, serverName, onRequestClose, onSave} = props;

  const [permissions, setPermissions] = useState<Map<string, PermissionState>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [originalServerUrl, setOriginalServerUrl] = useState('');
  const nameInput = useFormInput(serverName);
  const serverUrlInput = useFormInput('');
  const isNameDirty = nameInput.value.trim() !== serverName;
  const isServerUrlDirty = serverUrlInput.value.trim() !== originalServerUrl;

  // State for tools fetched from REST endpoint
  const [tools, setTools] = useState<Array<ToolInfo>>([]);
  const [isLoadingTools, setIsLoadingTools] = useState(false);
  const [toolsError, setToolsError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [authRedirectUrl, setAuthRedirectUrl] = useState<string | null>(null);

  // Function to refresh tools from MCP server via REST endpoint
  const refreshTools = useCallback(async (): Promise<void> => {
    setIsLoadingTools(true);
    setToolsError(null);
    setNeedsAuth(false);
    setAuthRedirectUrl(null);

    try {
      const response = (await postJSONPromise({
        url: `/1/api/llm/mcp-server-configs/${serverId}/refresh-tools`,
        data: {},
      })) as RefreshToolsResponse;

      if (response.needsRedirect && response.redirectUrl) {
        setNeedsAuth(true);
        setAuthRedirectUrl(response.redirectUrl);
        setTools([]);
      } else if (response.tools) {
        setTools(
          response.tools.map(
            (t: {name: string; title: string | null; description: string; parameters: unknown}) => ({
              name: t.name,
              description: t.description,
              parameters: t.parameters,
            })
          )
        );
      } else {
        setTools([]);
      }
    } catch (e) {
      if (isServerRequestError(e)) {
        setToolsError('Unable to connect to MCP server. Please check the server URL.');
      } else {
        throw e;
      }
    } finally {
      setIsLoadingTools(false);
    }
  }, [serverId]);

  // Query for server config (without tools - those come from REST)
  const configQuery = useBenchlingQuery<
    AdminMcpConfigurationModalQuery,
    AdminMcpConfigurationModalQueryVariables
  >(ADMIN_MCP_CONFIG_QUERY, {
    variables: {serverId},
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data.mcpServerConfig) {
        // Initialize server URL (independent of tools/permissions)
        if (data.mcpServerConfig.serverUrl) {
          setOriginalServerUrl(data.mcpServerConfig.serverUrl);
          serverUrlInput.onChange({
            target: {value: data.mcpServerConfig.serverUrl},
          } as React.ChangeEvent<HTMLInputElement>);
        }

        // Initialize tool permissions (may be unavailable if tools fail to fetch)
        if (data.mcpServerConfig.toolPermissions) {
          const permMap = new Map<string, PermissionState>();
          for (const perm of data.mcpServerConfig.toolPermissions) {
            permMap.set(perm.toolName, perm.permission as McpToolPermissionValue);
          }
          setPermissions(permMap);
        }
      }
    },
  });

  // Fetch tools when modal opens
  useEffect(() => {
    refreshTools();
  }, [refreshTools]);

  // Derive state from query result
  const isLoading = configQuery.type === QueryResultType.PENDING || isLoadingTools;
  const hasError = configQuery.type === QueryResultType.ERROR;

  const handlePermissionChange = (toolName: string, value: McpToolPermissionValue | ''): void => {
    setPermissions((prev) => {
      const next = new Map(prev);
      if (value === '') {
        next.delete(toolName);
      } else {
        next.set(toolName, value);
      }
      return next;
    });
    setIsDirty(true);
  };

  const handleEnableAll = (): void => {
    setPermissions((prev) => {
      const next = new Map(prev);
      for (const tool of tools) {
        next.set(tool.name, 'AUTO');
      }
      return next;
    });
    setIsDirty(true);
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      const data: Record<string, unknown> = {};

      if (isNameDirty) {
        data.name = nameInput.value.trim();
      }

      if (isServerUrlDirty) {
        data.serverUrl = serverUrlInput.value.trim();
      }

      if (isDirty) {
        data.toolPermissions = Array.from(permissions.entries()).map(([toolName, permission]) => ({
          toolName,
          permission,
        }));
      }

      await putJSONPromise({
        url: `/1/api/llm/mcp-server-configs/${serverId}`,
        data,
      });

      notifications.success('Configuration saved successfully');
      setIsDirty(false);
      if (isServerUrlDirty) {
        setOriginalServerUrl(serverUrlInput.value.trim());
        // Refresh tools from MCP server since the server URL changed
        await refreshTools();
      }
      onSave?.();
    } catch (e) {
      if (isServerRequestError(e)) {
        notifications.error('Failed to save configuration');
      } else {
        throw e;
      }
    } finally {
      setIsSaving(false);
    }
  };

  const renderAccessControl = (tool: ToolInfo): JSX.Element => (
    <SelectDropdown
      displayName={getPermissionLabel(permissions.get(tool.name) ?? null)}
      menu={
        <Menu value={permissions.get(tool.name) ?? ''}>
          <Option
            value="AUTO"
            onSelect={() => {
              handlePermissionChange(tool.name, 'AUTO');
            }}
          >
            {getPermissionLabel('AUTO')}
          </Option>
          <Option
            value="DISABLED"
            onSelect={() => {
              handlePermissionChange(tool.name, 'DISABLED');
            }}
          >
            {getPermissionLabel('DISABLED')}
          </Option>
        </Menu>
      }
    />
  );

  const hasChanges = isDirty || isNameDirty || isServerUrlDirty;

  return (
    <Modal
      title={
        <Group spacing={8}>
          <Badge color="blue" size="sm" radius="sm">
            Admin
          </Badge>
          <span>Manage Configuration - {serverName}</span>
        </Group>
      }
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSave}
      buttons={[
        {
          text: isSaving ? 'Saving...' : 'Save',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: isLoading || isSaving || !hasChanges,
        },
        {text: 'Cancel', cancel: true},
      ]}
    >
      <div className={css(styles.modalContent)}>
        <div className={css(styles.configSection)}>
          <FormGroup>
            <TextInput
              label="Server Name"
              value={nameInput.value}
              onChange={nameInput.onChange}
              maxLength={255}
            />
          </FormGroup>
          <FormGroup>
            <TextInput
              label="Server URL"
              value={serverUrlInput.value}
              onChange={serverUrlInput.onChange}
              maxLength={2048}
              disabled={isLoading}
            />
          </FormGroup>
        </div>

        <div className={css(styles.sectionDivider)} />
        <div className={css(styles.toolsSection)}>
          <div className={css(styles.toolPermissionsHeader)}>
            <div className={css(styles.toolPermissionsTitleRow)}>
              <Text className={css(styles.sectionTitle)}>Tool permissions</Text>
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                size={ButtonSize.SMALL}
                onClick={handleEnableAll}
                disabled={isLoading || tools.length === 0}
              >
                Grant All
              </Button>
            </div>
            <Text className={css(styles.sectionDescription)}>
              Manage which tools are available to this connector.
            </Text>
          </div>

          <div className={css(styles.toolListContainer)}>
            {isLoading ? (
              <div className={css(sharedStyles.loadingState)}>
                <BenchlingIcon icon="refresh" className={css(sharedStyles.spinningIcon)} />
                <Text>Loading tools...</Text>
              </div>
            ) : needsAuth && authRedirectUrl ? (
              <McpAuthPrompt authRedirectUrl={authRedirectUrl} onAuthComplete={refreshTools} />
            ) : (
              <McpToolList
                tools={tools}
                error={toolsError ?? (hasError ? 'Failed to load configuration.' : null)}
                renderAccessControl={renderAccessControl}
                expandable={true}
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContent: {
    minHeight: 300,
    maxHeight: 500,
    display: 'flex',
    flexDirection: 'column',
  },
  toolListContainer: {
    flex: 1,
    overflow: 'auto',
  },
  toolPermissionsHeader: {
    marginBottom: 16,
  },
  toolPermissionsTitleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionDescription: {
    fontSize: 13,
    color: GRAY_SEMIDARK,
    marginTop: 4,
  },
  configSection: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: NEAR_BLACK,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: GRAY_LIGHT,
    marginBottom: 16,
  },
  toolsSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
});
